import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Dumbbell, ArrowLeft, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

// Import class detail components
import {
  ClassInfoSection,
  BookingSection,
  PaymentSection,
  ReviewsSection,
  BookingConfirmation
} from '../components/classdetail';

const ClassDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingId, setBookingId] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [paidAmount, setPaidAmount] = useState(null);
  const [spots, setSpots] = useState(1);
  
  // Discount code state
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  
  // Cancellation policy state
  const [showCancellationTerms, setShowCancellationTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [userHasBooking, setUserHasBooking] = useState(false);
  const [userHasReviewed, setUserHasReviewed] = useState(false);

  const fetchClass = useCallback(async () => {
    try {
      const response = await apiClient.get(`/classes/${id}`);
      setClassData(response.data);
    } catch (error) {
      toast.error('Failed to load class details');
      navigate('/classes');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchReviews = useCallback(async () => {
    try {
      const response = await apiClient.get(`/reviews/${id}`);
      setReviews(response.data);
      if (user) {
        const userReview = response.data.find(r => r.user_id === user.user_id);
        setUserHasReviewed(!!userReview);
      }
    } catch (error) {
      console.error('Failed to fetch reviews:', error.message);
    }
  }, [id, user]);

  const checkUserBooking = useCallback(async () => {
    try {
      const response = await apiClient.get('/bookings');
      const hasCompletedBooking = response.data.some(
        b => b.class_id === id && b.payment_status === 'completed'
      );
      setUserHasBooking(hasCompletedBooking);
    } catch (error) {
      console.error('Failed to check user booking:', error.message);
    }
  }, [id]);

  useEffect(() => {
    fetchClass();
    fetchReviews();
  }, [fetchClass, fetchReviews]);

  useEffect(() => {
    if (user) {
      checkUserBooking();
    }
  }, [user, checkUserBooking]);

  const handleBooking = async () => {
    if (!user) {
      toast.error('Please login to book a class');
      navigate('/login');
      return;
    }
    
    if (!showCancellationTerms) {
      setShowCancellationTerms(true);
      return;
    }
    
    if (!acceptedTerms) {
      toast.error('Please accept the cancellation terms to proceed');
      return;
    }

    try {
      const response = await apiClient.post('/bookings', { class_id: id, spots: spots });
      const newBookingId = response.data.booking_id;
      setBookingId(newBookingId);
      setShowPayment(true);
      setShowCancellationTerms(false);
      toast.success(`Booking created for ${spots} spot${spots > 1 ? 's' : ''}! Please complete payment.`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to book class';
      toast.error(errorMsg);
      
      if (errorMsg.includes('Already booked')) {
        setTimeout(() => navigate('/dashboard'), 2000);
      }
    }
  };

  const handleWaitlist = async () => {
    if (!user) {
      toast.error('Please login to join waitlist');
      navigate('/login');
      return;
    }

    try {
      await apiClient.post(`/waitlist/${id}`);
      toast.success('Added to waitlist!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join waitlist');
    }
  };

  const handlePayByCash = async () => {
    try {
      const discountParam = appliedDiscount ? `&discount_code=${appliedDiscount.code}` : '';
      const response = await apiClient.post(`/payments/pay-cash?booking_id=${bookingId}${discountParam}`);
      setPaidAmount(response.data.final_price);
      setPaymentConfirmed(true);
      setShowPayment(false);
      toast.success('Booking confirmed! Pay at reception before class.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm booking');
    }
  };

  const handlePaymentSuccess = (amount) => {
    setPaidAmount(amount);
    setPaymentConfirmed(true);
    setShowPayment(false);
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewComment.trim()) {
      toast.error('Please write a comment');
      return;
    }

    setSubmittingReview(true);
    try {
      await apiClient.post('/reviews', {
        class_id: id,
        rating: reviewRating,
        comment: reviewComment.trim()
      });
      toast.success('Review submitted successfully!');
      setShowReviewForm(false);
      setReviewComment('');
      setReviewRating(5);
      fetchReviews();
      fetchClass();
      setUserHasReviewed(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <Dumbbell className="w-8 h-8 text-primary" />
            <span className="font-heading text-2xl font-bold tracking-tight">T-Fit Inc Classes</span>
          </Link>
          <div className="flex gap-4">
            {user && (
              <Link to="/dashboard">
                <Button data-testid="nav-dashboard-btn">Dashboard</Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <Link to="/classes" className="inline-flex items-center gap-2 text-neutral-400 hover:text-primary mb-8" data-testid="back-to-classes-link">
            <ArrowLeft className="w-4 h-4" />
            Back to Classes
          </Link>

          {paymentConfirmed ? (
            <BookingConfirmation
              classData={classData}
              bookingId={bookingId}
              paidAmount={paidAmount}
              appliedDiscount={appliedDiscount}
              userEmail={user?.email}
            />
          ) : (
            <>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="grid md:grid-cols-2 gap-12"
              >
                {/* Image */}
                <div className="relative rounded-sm overflow-hidden h-[400px]">
                  <img
                    src={classData.image_url}
                    alt={classData.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  {/* Rating Badge */}
                  {classData.review_count > 0 && (
                    <div className="absolute top-4 right-4 bg-black/60 backdrop-blur-sm px-3 py-2 rounded-sm flex items-center gap-2">
                      <Star className="w-5 h-5 fill-amber-400 text-amber-400" />
                      <span className="text-lg font-bold">{classData.average_rating?.toFixed(1) || '0.0'}</span>
                      <span className="text-sm text-neutral-400">({classData.review_count} reviews)</span>
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="space-y-6">
                  <ClassInfoSection 
                    classData={classData} 
                    appliedDiscount={appliedDiscount} 
                  />

                  {!showPayment ? (
                    <BookingSection
                      user={user}
                      classData={classData}
                      spots={spots}
                      setSpots={setSpots}
                      showCancellationTerms={showCancellationTerms}
                      acceptedTerms={acceptedTerms}
                      setAcceptedTerms={setAcceptedTerms}
                      onBooking={handleBooking}
                      onWaitlist={handleWaitlist}
                    />
                  ) : (
                    <PaymentSection
                      bookingId={bookingId}
                      classData={classData}
                      discountCode={discountCode}
                      setDiscountCode={setDiscountCode}
                      appliedDiscount={appliedDiscount}
                      setAppliedDiscount={setAppliedDiscount}
                      validatingDiscount={validatingDiscount}
                      setValidatingDiscount={setValidatingDiscount}
                      onPaymentSuccess={handlePaymentSuccess}
                      onPayByCash={handlePayByCash}
                      onCancel={() => {
                        setShowPayment(false);
                        navigate('/dashboard');
                      }}
                    />
                  )}
                </div>
              </motion.div>

              {/* Reviews Section */}
              <ReviewsSection
                reviews={reviews}
                user={user}
                userHasBooking={userHasBooking}
                userHasReviewed={userHasReviewed}
                showReviewForm={showReviewForm}
                setShowReviewForm={setShowReviewForm}
                reviewRating={reviewRating}
                setReviewRating={setReviewRating}
                reviewComment={reviewComment}
                setReviewComment={setReviewComment}
                submittingReview={submittingReview}
                onSubmitReview={handleSubmitReview}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassDetail;
